/* tslint:disable:max-classes-per-file */
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import store from '@/store/store';
import uuidv4 from 'uuid/v4';
import { GlobalTokenManager } from '@/classes/axios.cancel.token';

export class ApiBaseService {

  private http: AxiosInstance;
  private httpExternal: AxiosInstance;
  private internalToken: string | null = null;
  private baseUrl: string;

  public get token(): string | null {
    return this.internalToken || '';
  }

  public set token(value: string | null) {
    this.internalToken = value;
    this.http.defaults.headers.common.Authorization = `Bearer ${value}`;
  }

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    const options: any = {
      baseURL: this.baseUrl,
    };

    this.http = axios.create(options);
    this.httpExternal = axios.create();

    this.http.interceptors.request.use((config: any) => {
      if (!config.bypassCancelSignal) {
        config.signal = GlobalTokenManager.getSignal();
      }
      return config;
    });
    this.httpExternal.interceptors.request.use((config: any) => {
      if (!config.bypassCancelSignal) {
        config.signal = GlobalTokenManager.getSignal();
      }
      return config;
    });
    this.http.interceptors.response.use((resp: any) => resp, this.handleError);

    this.httpExternal.interceptors.response.use((resp: any) => resp, this.handleError);
  }

  public removeToken() {
    this.http.defaults.headers.common.Authorization = '';
  }

  public async delete<T>(uri: string, payload: any = null, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      const response = await this.http.delete(this.baseUrl + uri, { ...defConfig, data: payload });
      return response ? response.data : null;
  }

  public async deleteExt<T>(uri: string, payload: any, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      const response = await this.httpExternal.delete(uri, { ...defConfig, data: payload });
      return response ? response.data : null;
  }

  public async get<T>(uri: string, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      const response = await this.http.get(this.baseUrl + uri, defConfig);
      return response ? response.data : null;
  }

  public async getExt<T>(uri: string, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      const response = await this.httpExternal.get(uri, defConfig);
      return response ? response.data : null;
  }

  public async getBlob<T>(uri: string, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      defConfig.responseType = 'blob';
      const response = await this.http.get(this.baseUrl + uri, defConfig);
      return response ? response.data : null;
  }

  public async getBlobExt<T>(uri: string, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      defConfig.responseType = 'blob';
      const response = await this.httpExternal.get(uri, defConfig);
      return response ? response.data : null;
  }

  public async patch<T>(uri: string, payload: any, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      const response = await this.http.patch(this.baseUrl + uri, payload, defConfig);
      return response ? response.data : null;
  }

  public async patchExt<T>(uri: string, payload: any, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      const response = await this.httpExternal.patch(uri, payload, defConfig);
      return response ? response.data : null;
  }

  public async post<T>(uri: string, payload: any, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      const response = await this.http.post(this.baseUrl + uri, payload, defConfig);
      return response ? response.data : null;
  }

  public async postExt<T>(uri: string, payload: any, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      const response = await this.httpExternal.post(uri, payload, defConfig);
      return response ? response.data : null;
  }

  public async postBlob<T>(uri: string, blob: Blob, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      defConfig.headers = { ...defConfig.headers, 'content-type': blob.type };
      const response = await this.http.post(this.baseUrl + uri, blob, defConfig);
      return response ? response.data : null;
  }

  public async postBlobExt<T>(uri: string, blob: Blob, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      defConfig.headers = { ...config.headers, 'content-type': blob.type };
      const response = await this.httpExternal.post(uri, blob, defConfig);
      return response ? response.data : null;
  }

  public async postForm<T>(uri: string, formData: FormData, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      defConfig.headers = { ...defConfig.headers, 'content-type': 'multipart/form-data' };
      const response = await this.http.post(this.baseUrl + uri, formData, defConfig);
      return response ? response.data : null;
  }

  public async postFormExt<T>(uri: string, formData: FormData, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      defConfig.headers = { ...defConfig.headers, 'content-type': 'multipart/form-data' };
      const response = await this.httpExternal.post(uri, formData, defConfig);
      return response ? response.data : null;
  }

  public async put<T>(uri: string, payload: any, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      const response = await this.http.put(this.baseUrl + uri, payload, defConfig);
      return response ? response.data : null;
  }

  public async putExt<T>(uri: string, payload: any, config: any = {}): Promise<T> {
      const defConfig = this.generateDefaultConfig(config);
      const response = await this.httpExternal.put(uri, payload, defConfig);
      return response ? response.data : null;
  }

  public async head(uri: string, config: any = {}): Promise<void> {
    const defConfig = this.generateDefaultConfig(config);
    const response = await this.http.head(this.baseUrl + uri, defConfig);
    return response ? response.data : null;
}

public async headExt(uri: string, config: any = {}): Promise<void> {
    const defConfig = this.generateDefaultConfig(config);
    const response = await this.httpExternal.head(uri, defConfig);
    return response ? response.data : null;
}

  private handleError(error: any) {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (error.config && error.config.ignoreError && error.response) {
      return Promise.resolve(error.response);
    }

    if (error.response) {
      if (error.response.status === 401) {
          store.dispatch('userLogout');
      } else if (error.response.data && (error.response.data.error || error.response.data.errors)) {
        error.message = error.response.data.error || error.response.data.errors[0];
      }

      if (error.config.noToastFilters) {
        if (Array.isArray(error.config.noToastFilters)) {
          const results = error.config.noToastFilters.map((filter: any) => {
            if ((filter.status && filter.status === error.response.status)
                  || (filter.errorMessage && error.message && error.message.includes(filter.errorMessage))) {
              return filter.gives ? filter.gives(error.response) : error.response;
            }
            return undefined;
          })
          .filter((r: any) => r !== undefined);

          if (results.length) {
            return Promise.resolve(results[0]);
          }
        }
      }
    }

    return Promise.reject(error);
  }

  private generateDefaultConfig(config: any): any {
    return {
      headers: {},
      uuid: {
        requestUUID: uuidv4(),
      },
      ...config,
    };
  }
}

class ApiServiceManager {
  private services: ApiBaseService[];
  constructor(...services: ApiBaseService[]) {
      this.services = services;
  }

  public setToken(token: string) {
    for (const service of this.services) {
      service.token = token;
    }
  }

  public removeToken() {
    for (const service of this.services) {
      service.removeToken();
    }
  }
}

export const IntegrationsServiceAPI = new ApiBaseService(process.env.VUE_APP_INTEGRATIONS_SERVICE_API_URL!);
export const DataServiceAPI = new ApiBaseService(process.env.VUE_APP_DATA_SERVICE_API_URL!);
export const ApiService = new ApiBaseService(process.env.VUE_APP_API_URL!);
export const ApiV2Service = new ApiBaseService(process.env.VUE_APP_API_V2_URL!);
export const ApplicationsAPI = new ApiBaseService(process.env.VUE_APP_APPLICATIONS_API_URL!);

export const ApiManager = new ApiServiceManager(IntegrationsServiceAPI, DataServiceAPI, ApiService, ApiV2Service, ApplicationsAPI);
